/**
 * 聪明钱实时交易侧边栏
 * 显示推荐交易员的实时交易活动
 */
import { useEffect, useMemo, useState } from 'react';
import {
  FiX,
  FiCopy,
  FiUserPlus,
  FiTrendingUp,
  FiClock,
  FiTarget,
  FiDollarSign,
  FiLayers,
  FiPercent,
  FiActivity,
  FiPauseCircle,
  FiPlay,
} from 'react-icons/fi';
import { openBotDeepLink } from '../utils/deepLink';
import { useToast } from '../hooks/useToast';
import { useI18n } from '../i18n/useI18n';
import { useAuth } from '../contexts/AuthContext';
import { CopyTradeConfigModal } from './CopyTradeConfigModal';
import type { TraderActivity } from '../hooks/useTraderActivityWebSocket';

// 读取 Telegram Bot 用户名
const BOT_USERNAME = import.meta.env.VITE_BOT_USERNAME || 'PolyOneAI_bot';

/**
 * 构建复制交易 deep link
 */
function buildCopyTradeDeepLink(activityId: string): string {
  return `https://t.me/${BOT_USERNAME}?start=copy_${activityId}`;
}

interface SmartMoneyTradesSidebarProps {
  activities: TraderActivity[];
  isConnected: boolean;
  onClose?: () => void;
  showClose?: boolean;
  variant?: 'sidebar' | 'page';
}

type CopyConfigTrader = {
  address: string;
  name?: string;
  secondaryName?: string;
  winRate?: number | null;
  totalProfit?: number | null;
  aiScore?: number | null;
  tradingStyle?: string | null;
};

export function SmartMoneyTradesSidebar({
  activities,
  isConnected,
  onClose,
  showClose,
  variant = 'sidebar',
}: SmartMoneyTradesSidebarProps) {
  const { isAuthenticated, setShowLoginModal } = useAuth();
  const { pushToast } = useToast();
  const { t } = useI18n();
  const [isPaused, setIsPaused] = useState(false);
  const [displayActivities, setDisplayActivities] = useState<TraderActivity[]>([]);
  const [copyConfigOpen, setCopyConfigOpen] = useState(false);
  const [copyConfigTrader, setCopyConfigTrader] = useState<CopyConfigTrader | null>(null);

  const showCloseButton = showClose ?? variant === 'sidebar';

  // OPTIMIZATION: Reduced blur from xl to md, used specific hex with opacity for better performance
  const containerClassName =
    variant === 'page'
      ? 'w-full flex flex-col'
      : `fixed right-0 w-full md:w-[420px] z-50 bg-[#0B0E14]/95 backdrop-blur-md border-l border-white/5 flex flex-col shadow-2xl animate-slide-in-right
        top-[92px] md:top-[108px] lg:top-[128px]
        h-[calc(100vh-92px)] md:h-[calc(100vh-108px)] lg:h-[calc(100vh-128px)]`;

  const listClassName =
    variant === 'page'
      ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 px-6 py-6'
      : 'flex-1 overflow-y-auto px-4 py-4 space-y-3 scrollbar-thin scrollbar-track-transparent scrollbar-thumb-white/10';

  const emptyStateClassName =
    variant === 'page'
      ? 'col-span-full flex flex-col items-center justify-center py-12 text-slate-500'
      : 'flex flex-col items-center justify-center h-full text-slate-500';

  const normalizeTimestampMs = (value: number): number => {
    if (!Number.isFinite(value)) return 0;
    return value > 1_000_000_000_000 ? value : value * 1000;
  };

  const getActivityTimeMs = (activity: TraderActivity): number => {
    if (activity.received_at && Number.isFinite(activity.received_at)) {
      return activity.received_at;
    }
    return normalizeTimestampMs(activity.timestamp);
  };

  const sortedActivities = useMemo(() => {
    const withIndex = activities.map((activity, index) => ({ activity, index }));
    withIndex.sort((a, b) => {
      const timeA = getActivityTimeMs(a.activity);
      const timeB = getActivityTimeMs(b.activity);
      if (timeA !== timeB) {
        return timeB - timeA;
      }
      return a.index - b.index;
    });
    return withIndex.map((entry) => entry.activity);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activities]);

  useEffect(() => {
    if (!isPaused) {
      setDisplayActivities(sortedActivities);
    }
  }, [sortedActivities, isPaused]);

  // REMOVED: Global setInterval that caused full re-renders every second.
  // Time update logic is moved to the TimeAgo component.

  const handleCopyTrade = (activity: TraderActivity): void => {
    const deepLink = buildCopyTradeDeepLink(activity.id);
    openBotDeepLink(deepLink);
    pushToast(t('smartMoneyTrades.jumpedToCopy'), 'action');
  };

  const openCopyConfigModal = (traderInfo: CopyConfigTrader): void => {
    setCopyConfigTrader(traderInfo);
    setCopyConfigOpen(true);
  };

  const closeCopyConfigModal = (): void => {
    setCopyConfigOpen(false);
  };

  const handleFollowTrader = (activity: TraderActivity): void => {
    if (!isAuthenticated) {
      pushToast(t('leaderboardTable.copyNeedLogin'), 'info');
      setShowLoginModal?.(true);
      return;
    }
    const name = activity.trader_name || shortAddress(activity.trader_address);
    openCopyConfigModal({
      address: activity.trader_address,
      name,
      winRate: activity.trader_stats?.win_rate ?? null,
      totalProfit: activity.trader_stats?.profit ?? null,
    });
  };

  const handleCopyAddress = (address: string): void => {
    navigator.clipboard.writeText(address);
    pushToast(t('smartMoneyTrades.addressCopied'), 'success');
  };

  const shortAddress = (address: string | null): string => {
    if (!address) return '';
    return `${address.slice(0, 4)}...${address.slice(-4)}`;
  };

  const formatSignedPercent = (value: number | null | undefined): string => {
    if (value === null || value === undefined || Number.isNaN(value)) return '-';
    return `${value >= 0 ? '+' : ''}${value.toFixed(1)}%`;
  };

  const formatPercent = (value: number | null | undefined): string => {
    if (value === null || value === undefined || Number.isNaN(value)) return '-';
    return `${value.toFixed(1)}%`;
  };

  const formatSignedUSD = (value: number | null | undefined): string => {
    if (value === null || value === undefined || Number.isNaN(value)) return '-';
    const sign = value >= 0 ? '+' : '';
    const absValue = Math.abs(value);
    if (absValue >= 1_000_000) return `${sign}$${(value / 1_000_000).toFixed(1)}M`;
    if (absValue >= 1_000) return `${sign}$${(value / 1_000).toFixed(1)}k`;
    return `${sign}$${value.toFixed(0)}`;
  };

  return (
    <div className={containerClassName}>
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-5 border-b border-white/5 bg-gradient-to-r from-white/[0.02] to-transparent">
        <div>
          <div className="flex items-center gap-3">
            <h2 className="text-lg font-bold flex items-center gap-2 text-white">
              <span className="p-1.5 rounded-lg bg-emerald-500/10 text-emerald-400">
                <FiActivity className="w-4 h-4" />
              </span>
              {t('smartMoneyTrades.title')}
            </h2>
            <div
              className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium transition-colors ${isPaused
                  ? 'bg-amber-500/10 text-amber-400'
                  : 'bg-emerald-500/10 text-emerald-400'
                }`}
            >
              <div className={`w-1.5 h-1.5 rounded-full ${isPaused ? 'bg-amber-400' : 'bg-emerald-400 animate-pulse'}`} />
              {isPaused ? t('smartMoneyTrades.paused') : t('smartMoneyTrades.live')}
            </div>
          </div>
          <p className="text-xs text-slate-500 mt-1 pl-9">
            {isConnected ? t('smartMoneyTrades.monitoring') : t('smartMoneyTrades.disconnected')}
          </p>
        </div>
        {showCloseButton && (
          <button
            type="button"
            onClick={() => onClose?.()}
            className="p-2 -mr-2 rounded-full text-slate-400 hover:text-white hover:bg-white/5 transition-colors"
            aria-label={t('smartMoneyTrades.close')}
          >
            <FiX className="text-xl" />
          </button>
        )}
      </div>

      {/* Activity List */}
      <div
        className={listClassName}
        onMouseEnter={() => setIsPaused(true)}
        onMouseLeave={() => setIsPaused(false)}
      >
        {displayActivities.length === 0 && (
          <div className={emptyStateClassName}>
            <div className="w-12 h-12 rounded-full bg-white/5 flex items-center justify-center mb-3">
              <FiClock className="text-xl opacity-40" />
            </div>
            <p className="text-sm font-medium">{t('smartMoneyTrades.noTrades')}</p>
            <p className="text-xs mt-1 opacity-60">{t('smartMoneyTrades.scanning')}</p>
          </div>
        )}

        {displayActivities.map((activity) => {
          const isBuy = activity.side === 'BUY';
          const rov = activity.trader_stats?.rov ?? null;
          const winRate = activity.trader_stats?.win_rate ?? null;
          const profit = activity.trader_stats?.profit ?? null;
          const timestamp = getActivityTimeMs(activity);

          return (
            <div
              key={activity.id}
              className="group relative rounded-[20px] p-5 transition-all duration-300 border border-white/5 bg-white/[0.02] hover:bg-white/[0.04] hover:shadow-xl hover:shadow-black/20 hover:-translate-y-0.5"
            >
              {/* Header Row: Trader & Time */}
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className={`w-1 rounded-full h-8 ${isBuy ? 'bg-emerald-500' : 'bg-rose-500'}`} />
                  <div className="flex flex-col">
                    <div className="flex items-center gap-2">
                      <span className="font-bold text-sm text-slate-200">
                        {activity.trader_name || shortAddress(activity.trader_address)}
                      </span>
                      <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded uppercase ${isBuy ? 'bg-emerald-500/20 text-emerald-400' : 'bg-rose-500/20 text-rose-400'
                        }`}>
                        {isBuy ? 'Buy' : 'Sell'}
                      </span>
                    </div>
                    <button
                      onClick={() => handleCopyAddress(activity.trader_address)}
                      className="text-xs text-slate-500 hover:text-white transition-colors text-left flex items-center gap-1"
                    >
                      {shortAddress(activity.trader_address)}
                      <FiCopy className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                    </button>
                  </div>
                </div>
                <span className="text-[10px] font-medium text-slate-500 bg-white/5 px-2 py-1 rounded-full">
                  <TimeAgo timestamp={timestamp} />
                </span>
              </div>

              {/* Stats Row - Clean & Minimal */}
              <div className="flex items-center divide-x divide-white/10 bg-white/[0.02] rounded-xl py-3 px-1 mb-4">
                <div className="flex-1 flex flex-col items-center gap-0.5">
                  <span className="text-[10px] text-slate-500 uppercase tracking-wider">{t('smartMoneyTrades.rov')}</span>
                  <span className={`text-sm font-bold ${rov !== null && rov < 0 ? 'text-rose-400' : 'text-violet-400'
                    }`}>{formatSignedPercent(rov)}</span>
                </div>
                <div className="flex-1 flex flex-col items-center gap-0.5">
                  <span className="text-[10px] text-slate-500 uppercase tracking-wider">{t('smartMoneyTrades.winRate')}</span>
                  <span className="text-sm font-bold text-emerald-400">{formatPercent(winRate)}</span>
                </div>
                <div className="flex-1 flex flex-col items-center gap-0.5">
                  <span className="text-[10px] text-slate-500 uppercase tracking-wider">{t('smartMoneyTrades.profit')}</span>
                  <span className={`text-sm font-bold ${profit !== null && profit < 0 ? 'text-rose-400' : 'text-emerald-400'
                    }`}>{formatSignedUSD(profit)}</span>
                </div>
              </div>

              {/* Market Info */}
              <div className="mb-4 space-y-3">
                <p className="text-sm font-medium text-slate-300 leading-snug line-clamp-2">
                  {activity.market_title}
                </p>

                <div className="flex items-center justify-between text-xs p-2.5 rounded-lg bg-white/5 border border-white/5">
                  <div className="flex items-center gap-3">
                    <div className="flex flex-col">
                      <span className="text-[10px] text-slate-500">Pick</span>
                      <span className="font-bold text-sky-400">{activity.outcome}</span>
                    </div>
                    <div className="w-px h-6 bg-white/10" />
                    <div className="flex flex-col">
                      <span className="text-[10px] text-slate-500">Price</span>
                      <span className="font-medium text-slate-200">${activity.price.toFixed(2)}</span>
                    </div>
                    <div className="w-px h-6 bg-white/10" />
                    <div className="flex flex-col">
                      <span className="text-[10px] text-slate-500">Size</span>
                      <span className="font-medium text-slate-200">{activity.size.toFixed(0)}</span>
                    </div>
                  </div>
                  <div className="text-right">
                    <span className="text-[10px] text-slate-500 block">Total</span>
                    <span className="text-emerald-400 font-bold">${(activity.price * activity.size).toFixed(0)}</span>
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="grid grid-cols-2 gap-2 mt-auto">
                <button
                  type="button"
                  onClick={() => handleCopyTrade(activity)}
                  className="flex items-center justify-center gap-2 px-3 py-2.5 rounded-xl bg-sky-500/10 hover:bg-sky-500/20 text-sky-400 text-xs font-bold transition-all active:scale-95 group/btn"
                >
                  <FiCopy className="w-3.5 h-3.5 group-hover/btn:scale-110 transition-transform" />
                  {t('smartMoneyTrades.copyTrade')}
                </button>
                <button
                  type="button"
                  onClick={() => handleFollowTrader(activity)}
                  className="flex items-center justify-center gap-2 px-3 py-2.5 rounded-xl bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-400 text-xs font-bold transition-all active:scale-95 group/btn"
                >
                  <FiUserPlus className="w-3.5 h-3.5 group-hover/btn:scale-110 transition-transform" />
                  {t('smartMoneyTrades.followTrader')}
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Footer */}
      <div className="px-6 py-3 border-t border-white/5 bg-black/20">
        <p className="text-[10px] text-slate-500 text-center">
          {t('smartMoneyTrades.footerText', { count: displayActivities.length })}
        </p>
      </div>

      <CopyTradeConfigModal
        open={copyConfigOpen}
        onClose={closeCopyConfigModal}
        trader={copyConfigTrader}
      />
    </div>
  );
}

// Optimization: Separate component for time updates to prevent full sidebar re-renders
function TimeAgo({ timestamp }: { timestamp: number }) {
  const { t } = useI18n();
  const [_, setTick] = useState(0);

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      setTick((t) => t + 1);
    }, 1000);
    return () => window.clearInterval(intervalId);
  }, []);

  const diff = Math.max(0, Math.floor((Date.now() - timestamp) / 1000));

  if (diff < 60) return <>{t('smartMoneyTrades.secondsAgo', { n: diff })}</>;
  if (diff < 3600) return <>{t('smartMoneyTrades.minutesAgo', { n: Math.floor(diff / 60) })}</>;
  if (diff < 86400) return <>{t('smartMoneyTrades.hoursAgo', { n: Math.floor(diff / 3600) })}</>;
  return <>{t('smartMoneyTrades.daysAgo', { n: Math.floor(diff / 86400) })}</>;
}
